<!--
Abstract heatmap component to display multiple heatmap layers. Child components must provide the HeatmapLayers.
-->
<template>
  <div class="heatmap">
    <transition name="fade-transition">
      <div v-if="loading" class="heatmap__loader-wrap"
        @mousedown.stop.prevent
        @mousewheel.stop.prevent
        @DOMMouseScroll.stop.prevent
        @dommousescroll.stop.prevent
        @scroll.stop.prevent>
        <!--Trying to prevent mouse events from getting propagated to the map covered by the loader overlay. This works in
        Chrome, and only partially works in Edge and FF. The heatmap bloom radius can get messed up when the user zooms while
        the map is loading. TODO
        This problem could be fixed by disabling interactive map features while the data is loading. -->
        <v-progress-circular
          v-if="true"
          indeterminate
          color="primary"
          class="heatmap__loader"
          size="60"
        />
      </div>
    </transition>
    <div class="heatmap-header--container">
      <div class="heatmap-header--text d-flex align-center justify-center">
        Click & Drag on Map to Explore
      </div>
    </div>
    <transition name="fade-transition">
      <span v-if="showError" class="heatmap__msg error alert">The service is currently not available.</span>
    </transition>
  </div>
</template>

<script>
import L from 'leaflet'
import { eventBus, viewportSize } from '@/utils'
import Vue from 'vue'
import { mapActions, mapState } from 'vuex'
import constants from './heatmap_variables'
import axios from 'axios'
import markerCfg from './marker_config'
import airbrake from '@/helpers/airbrake'

const geoCodingApi = APP_CONFIG.mapbox.geo_coding_api
const mapboxToken = APP_CONFIG.mapbox.access_token

const MD_VIEWPORT_MIN_WIDTH = 960 // as defined by MDL. TODO [balazs] - should be shared with style files
const MAX_MAP_HEIGHT = parseInt(constants['$map-max-height-int'], 10)

const USA_BOUNDS = [
  [24.5554, -124.639563], // southeast: top, right
  [49.327238, -67.015371] // northeast: bottom, left
]

/**
 * An economic utility to intercept frequently emitted native events to relay them
 * via a vue event bus right before the browser gets repainted.
 * @param cfg {Object} config options. All but the source option are required
 * @param cfg.source  native event source, e.g. window
 * @param cfg.nativeEventType  the type of the native event, e.g. 'resize'
 * @param cfg.vueEventType  the name of the event used when the native event is redistributed via the vue event bus
 * @param cfg.bus  the vue event bus
 */
function throttle (cfg) {
  cfg.source = cfg.source || window
  let running = false
  const func = function () {
    if (running) { return }
    running = true
    let args = arguments
    requestAnimationFrame(function () {
      cfg.bus.$emit(cfg.vueEventType, args)
      running = false
    })
  }
  cfg.source.addEventListener(cfg.nativeEventType, func)
}

function getLeafletBounds (bounds) {
  return [
    [parseFloat(bounds.top), parseFloat(bounds.right)], // southeast: top, right
    [parseFloat(bounds.bottom), parseFloat(bounds.left)] // northeast: bottom, left
  ]
}

function leafletBoundsForBBString (boundingBoxString) {
  // 'west_long, south_lat, east_long, north_lat'
  const parts = boundingBoxString.replace(/_/g, '.').split(',')
  return [
    [parseFloat(parts[1]), parseFloat(parts[0])],
    [parseFloat(parts[3]), parseFloat(parts[2])]
  ]
}

export default {
  props: {
    // { geoType, tilesUrl, eventBusId, leftPadding?, markerAddress?}
    config: {
      required: true,
      type: Object,
      validator: function (cfg) {
        let valid = true
        valid = valid && cfg.tilesUrl && cfg.tilesUrl.indexOf('http') === 0
        valid = valid && typeof (cfg.geoType) === 'string'
        valid = valid && typeof (cfg.eventBusId) === 'string'
        valid = valid && (cfg.leftPadding === undefined || typeof cfg.leftPadding === 'number')

        return valid
      }
    },
    interactive: {
      type: Boolean,
      default: true
    }
  },

  mounted () {
    this.bus = eventBus(this.config.eventBusId)
    this.adaptToViewportSize()
    this.getInitialBounds(bounds => { // TODO [balazs] - use string bounds
      const cfg = this.config
      const lMap = this.createLeafletMap(getLeafletBounds(bounds), [this.leftPadding(), 0])
      L.control.zoom({position: 'topright'}).addTo(lMap)
      this.initHeatmapLayers()

      if (cfg.markerAddress) {
        this.addAddressMarker()
      }
    }, err => { // onError
      if (!this.lMap) {
        this.createLeafletMap()
      }
      this.loading = false
      this.showError = true
      airbrake.notify(err)
    })
  },

  data () {
    return {
      // private state
      loading: true,
      showError: false
    }
  },

  methods: {
    ...mapActions('heatmap', ['setMapBounds', 'setMapInited']),

    /**
     * @return String The map bounds as a string ready to be used in API calls
     */
    currentBounds () {
      return this.lMap.getBounds().toBBoxString().replace(/\./g, '_')
    },

    findLayerById (layerId) {
      if (this.heatmapLayers) {
        for (let i = 0; i < this.heatmapLayers.length; i++) {
          if (this.heatmapLayers[i].id === layerId) {
            return this.heatmapLayers[i]
          }
        }
      }
    },

    addAddressMarker () {
      if (geoCodingApi && mapboxToken) {
        const address = this.config.markerAddress
        axios.get(`${geoCodingApi}${encodeURIComponent(address.full)}.json`, {
          params: {
            access_token: mapboxToken,
            autocomplete: false,
            country: 'us',
            types: 'address'
          }
        }).then(response => {
          const mapBoxFeatures = response.data.features
          if (mapBoxFeatures &&
            mapBoxFeatures[0] &&
            mapBoxFeatures[0].relevance > 0.6 && // addresses with a low relevance are likely to be wrong matches and it's better not to display them. (DOHA-1270)
            mapBoxFeatures[0].center) {
            const coords = mapBoxFeatures[0].center
            L.marker([coords[1], coords[0]], {icon: L.icon(markerCfg)})
              .bindPopup(`<h6>HQ Address</h6><p>${address.line1}</p><p>${address.line2}</p>`)
              .addTo(this.lMap)
          } else { // either the API didn't return anything useful or the relevance requirement is unmet
            this.addLocationMarker()
          }
        }).catch(err => {
          this.addLocationMarker()
          airbrake.notify(err)
        })
      }
    },

    addLocationMarker () {
      const address = this.config.markerAddress
      const location = address.location
      if (location) {
        L.marker([location.lat, location.lon], {icon: L.icon(markerCfg)})
          .bindPopup(`<h6>Headquarters</h6><p>${address.line2}</p>`)
          .addTo(this.lMap)
      }
    },

    /**
     * Calculate padding if the map has an overlay on it
     */
    leftPadding () {
      const overlayOnMap = viewportSize().w >= MD_VIEWPORT_MIN_WIDTH
      let left = 0
      if (this.selectedGeo.type !== 'GeoBounds' && (this.config.forceLeftPadding || overlayOnMap)) {
        left = this.config.leftPadding > 0 ? this.config.leftPadding : 285
      }
      return left
    },

    createLeafletMap (bounds = USA_BOUNDS, paddingTopLeft = [0, 0]) {
      const interactive = this.interactive
      const lMap = this.lMap = L.map(this.$el, {
        minZoom: 3,
        maxZoom: 13,
        attributionControl: false,
        zoomControl: false,
        touchZoom: interactive,
        scrollWheelZoom: false,
        boxZoom: interactive,
        doubleClickZoom: interactive,
        dragging: interactive
      })
      this.tileLayer = L.tileLayer(this.config.tilesUrl, {tileSize: 512, zoomOffset: -1}).addTo(lMap)
      lMap.fitBounds(bounds, {
        paddingTopLeft: paddingTopLeft
      })

      this.setMapBounds({mapBounds: this.currentBounds()})
      Vue.nextTick(this.setMapInited)
      return lMap
    },

    /**
     * Make sure the map never occupies too much vertical space so that scrolling
     * is always possible. If the map took the whole viewport it'd tap on scroll
     * events.
     */
    adaptToViewportSize () {
      const setMapHeight = () => {
        if (this.mapHeight) { // explicitly set mapHeight takes precedence
          this.$el.style.height = this.mapHeight
        } else {
          const size = viewportSize()
          let newHeight
          // When the width is < 768px the stats panel will not be rendered on the map and it becomes
          // necessary to make the map shrink to avoid getting trapped on the map. When it's larger
          // it's always possible to scroll away on the overlay hence in that case there is no need to
          // adjust the map's height. (DOHA-1225)
          if (size.w < MD_VIEWPORT_MIN_WIDTH) {
            const lessThanHalf = Math.round(size.h / 2.3)
            // height is 1/2.3 of the viewport height or 400px, whichever is smaller
            newHeight = lessThanHalf > MAX_MAP_HEIGHT ? constants['$map-max-height'] : `${lessThanHalf}px`
          } else {
            newHeight = constants['$map-max-height']
          }
          if (this.$el.style.height !== newHeight) {
            this.bus.$emit('reset-map-height')
            this.$el.style.height = newHeight
          }
        }
      }

      setMapHeight()
      throttle({
        nativeEventType: 'resize',
        vueEventType: 'window-resize',
        bus: this.bus
      })
      this.bus.$on('window-resize', setMapHeight)
    },

    initHeatmapLayers () {
      this.heatmapLayers = this.createHeatmapLayers()
      this.addInitialLayerToMap()

      const overlays = {}
      this.heatmapLayers.forEach(hml => {
        overlays[hml.name] = hml.overlay
      })

      if (this.heatmapLayers.length > 1) {
        L.control.layers(overlays, null, {
          collapsed: false,
          position: 'bottomright'
        }).addTo(this.lMap)
      }
    },

    refreshLayers () {
      this.heatmapLayers.forEach(hml => {
        hml.refresh()
      })
    },

    /**
     * @return Array an array of HeatmapLayer objects to be added to the map in the order of how
     * they will be displayed in the layer selector widget.
     */
    createHeatmapLayers () {
      throw new Error('Heatmap.createHeatmapLayers() must be implemented by child classes')
    },

    addInitialLayerToMap () {
      throw new Error('Heatmap.addInitialLayerToMap() must be implemented by child classes')
    },

    /**
     * @param onSuccess a function to call with the initial map bounds
     * @param onError a function to call if getting the initial map bounds failed
     **/
    // eslint-disable-next-line no-unused-vars
    getInitialBounds (onSuccess, onError) {
      throw new Error('Heatmap.getInitialBounds() must be implemented by child classes')
    }
  },

  computed: {
    ...mapState('heatmap', ['heatmapState', 'selectedGeo'])
  },

  watch: {
    heatmapState (state) {
      if (state === 'LOADING') {
        this.loading = true
        this.showError = false
      } else if (state === 'LOADED') {
        this.loading = false
        this.showError = false
      } else if (state === 'FAILED') {
        this.loading = false
        this.showError = true
      }
    },

    selectedGeo (current, previous) {
      if (previous && current.id !== previous.id) {
        this.dispatchMapBoundsChange = false
        this.lMap.fitBounds(leafletBoundsForBBString(current.geo_bounds), {
          paddingTopLeft: [this.leftPadding(), 0],
          animate: false // important to avoid map synchronization issues
        })
        this.setMapBounds({mapBounds: this.currentBounds()})
        this.refreshLayers()
      }
    }
  }
}
</script>

<!--
  Do not add any style here because the webpack build process ignores them when this
  component is used by a child class loaded async via code splitting. See: DOHA-1349
-->



// WEBPACK FOOTER //
// frontend/src/components/heatmap/Heatmap.vue